import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import {
  fetchNotifications,
  RootState,
  useAppDispatch,
  useAppSelector,
} from "@hypexone/uicore";
import { useConstantMarqueeSpeed } from "../MarqueeText/useConstantMarqueeSpeed";
import "./announcement.styles.css";

type NotificationItem = {
  id?: number | string;
  title?: string;
  message?: string;
  startTime?: string | number | Date;
};

const formatNotificationTime = (startTime?: string | number | Date) => {
  if (startTime == null || startTime === "") return "";
  const parsed = moment(startTime);
  return parsed.isValid() ? parsed.format("DD MMM, HH:mm") : "";
};

const Announcement: React.FC = () => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(
    (state: RootState) => state.auth.notifications
  );
  const { langData } = useAppSelector((state) => state.cms);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Set<string | number>>(
    () => new Set()
  );

  const list: NotificationItem[] = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    return notifications.filter(Boolean);
  }, [notifications]);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const contentKey = useMemo(
    () => list.map((n) => n.message ?? "").join("|"),
    [list]
  );

  useConstantMarqueeSpeed(
    containerRef,
    trackRef,
    list.length > 0,
    contentKey,
    "loop"
  );

  const handleBellClick = () => {
    dispatch(fetchNotifications());
    setCollapsedIds(new Set());
    setIsModalOpen(true);
  };

  const toggleExpanded = (id: string | number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (list.length === 0) {
    return null;
  }

  const modal = isModalOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className={`relative flex w-[95%] max-w-md md:max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-[12px] border-2 border-blue4 bg-blue2 px-3 py-3 shadow-lg`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-center justify-between pb-3">
              <h2 className="text-xl font-semibold text-white">
                {langData?.["notifications"] || "Notifications"}
              </h2>
              <button
                type="button"
                className="text-white p-1"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
              {list.map((announcement, index) => {
                const id = announcement.id ?? index;
                const isExpanded = !collapsedIds.has(id);
                const timeLabel = formatNotificationTime(announcement.startTime);
                const title = announcement.title?.trim() || "";
                const message = announcement.message?.trim() || "";

                return (
                  <li key={id} className="shrink-0 overflow-hidden rounded-md">
                    <div className="flex w-full items-center gap-2 bg-black1 px-2 py-1.5 text-white">
                      <button
                        type="button"
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-blue4 transition-transform ${
                          isExpanded ? "" : "rotate-180"
                        }`}
                        onClick={() => toggleExpanded(id)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                        </svg>
                      </button>
                      {timeLabel ? (
                        <span className="text-xs sm:text-sm whitespace-nowrap">
                          {timeLabel}
                        </span>
                      ) : null}
                      <span className="truncate text-xs sm:text-sm font-medium">
                        {title || "Notification"}
                      </span>
                    </div>

                    {isExpanded && message ? (
                      <div className="bg-white px-3 py-2 text-xs sm:text-sm leading-snug">
                        {message}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="flex w-full text-sm font-bold overflow-hidden wrapper gap-1 items-center">
        <button
          type="button"
          className="flex-shrink-0 flex items-center justify-center w-6 h-6 text-white hover:opacity-80 transition-opacity cursor-pointer"
          onClick={handleBellClick}
          aria-label={langData?.["notifications"] || "Notifications"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-4 h-4"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2Zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2Z" />
          </svg>
        </button>

        <div
          className="md:w-[100%] w-full md:overflow-hidden marquee-text min-w-0 flex-1"
          ref={containerRef}
        >
          <div ref={trackRef} className="marquee-text-track">
            {list.map((announcement, index) => (
              <p key={`a-${index}`} className="pictures-container">
                <span>{announcement.message}</span>
              </p>
            ))}
            {list.map((announcement, index) => (
              <p key={`b-${index}`} className="pictures-container">
                <span>{announcement.message}</span>
              </p>
            ))}
          </div>
        </div>
      </div>

      {modal}
    </>
  );
};

export default Announcement;
